import React from 'react';

import { Typography } from '@sunwisesoftware/sunwise-ui';
import orderBy from 'lodash/orderBy';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useBreakpoint } from 'common/hooks';
import { panelTypeToLabel } from 'common/utils/helpers';

import ProductActionsFormatter from './ProductActionsFormatter';

const ProductColumns = ({
    filterData,
    handleClickBulkItems,
    handleClickUpdate,
    hiddenColumns,
    orders,
    rowModesModel,
    selectedRowId,
    setRowModesModel,
    setSelectedRowId,
}) => {
    const { t } = useTranslation();
    const breakpoint = useBreakpoint();

    let columns = [
        {
            align: 'left',
            display: 'flex',
            field: 'brand',
            flex: 1,
            headerName: t('Brand'),
            hide: hiddenColumns.includes('brand'),
            order: orders?.brand,
            valueGetter: (_, row) => row?.brand?.name,
        },
        {
            align: 'left',
            display: 'flex',
            field: 'code',
            flex: 1,
            headerName: t('Code'),
            hide: hiddenColumns.includes('code'),
            order: orders?.code,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography variant="caption" fontWeight="bold">
                        {row.code}
                    </Typography>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'product_type',
            flex: 1,
            headerName: t('Type'),
            hide: hiddenColumns.includes('product_type'),
            order: orders?.product_type,
            renderCell: (params) => {
                const { row } = params;
                return panelTypeToLabel(row.product_type);
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'max_power',
            flex: 1,
            headerName: t('Potency'),
            hide: hiddenColumns.includes('max_power'),
            order: orders?.max_power,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography variant="caption" fontWeight="bold">
                        {row.max_power} W
                    </Typography>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'name',
            flex: 1,
            headerName: t('Name'),
            hide: hiddenColumns.includes('name'),
            order: orders?.name,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography variant="caption" fontWeight="bold">
                        {row.name}
                    </Typography>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'height',
            flex: 1,
            headerName: t('Length'),
            hide: hiddenColumns.includes('height'),
            order: orders?.height,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography variant="caption" fontWeight="bold">
                        {row.height} mm
                    </Typography>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'width',
            flex: 1,
            headerName: t('Width'),
            hide: hiddenColumns.includes('width'),
            order: orders?.width,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography variant="caption" fontWeight="bold">
                        {row.width} mm
                    </Typography>
                );
            },
        },
        {
            align: 'left',
            display: 'flex',
            field: 'depth',
            flex: 1,
            headerName: t('Thickness'),
            hide: hiddenColumns.includes('depth'),
            order: orders?.depth,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography variant="caption" fontWeight="bold">
                        {row.depth} mm
                    </Typography>
                );
            },
        },
    ];

    if (['xs', 'sm'].includes(breakpoint)) {
        columns = [
            {
                align: 'left',
                display: 'flex',
                field: 'name',
                flex: 1,
                headerName: t('Name'),
                hide: hiddenColumns.includes('name'),
                order: orders?.name,
                renderCell: (params) => {
                    const { row } = params;
                    return (
                        <Typography variant="caption" fontWeight="bold">
                            {row.name}
                        </Typography>
                    );
                },
            },
        ];
    }

    columns = [
        ...columns,
        {
            align: 'left',
            display: 'flex',
            field: '',
            flex: 1,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <ProductActionsFormatter
                        filterData={filterData}
                        handleClickBulkItems={handleClickBulkItems}
                        handleClickUpdate={handleClickUpdate}
                        row={row}
                        rowModesModel={rowModesModel}
                        selectedRowId={selectedRowId}
                        setRowModesModel={setRowModesModel}
                        setSelectedRowId={setSelectedRowId}
                    />
                );
            },
            sortable: false,
        },
    ];

    return orderBy(columns, ['order'], ['asc']);
};

ProductColumns.propTypes = {
    filterData: PropTypes.object,
    handleClickBulkItems: PropTypes.func,
    handleClickUpdate: PropTypes.func,
    hiddenColumns: PropTypes.array,
    orders: PropTypes.object,
    rowModesModel: PropTypes.object,
    selectedRowId: PropTypes.func,
    setRowModesModel: PropTypes.func,
    setSelectedRowId: PropTypes.func,
};

export default ProductColumns;
